import { Box, ButtonBase, Skeleton } from "@mui/material";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import colors from "../../config/colors";
import { colorPalette } from "../../config/colorPalette";
import Banner from "../Banner/Banner";
import Checkbox from "../Checkbox/Checkbox";
import SearchField from "../SearchField/SearchField";

const { brand, mercury, mercuryAlpha } = colorPalette;

const FONT = '"Lexend", sans-serif';
const SELECTION_WIDTH = "28px";
const ACTIONS_WIDTH = "32px";
const DEFAULT_COLUMN_WIDTH = "minmax(120px, 1fr)";
const SKELETON_ROWS = 5;

const ellipsisSx = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const rowSx = (template, height) => ({
  display: "grid",
  gridTemplateColumns: template,
  columnGap: "12px",
  alignItems: "center",
  height,
  padding: "0 16px",
  boxSizing: "border-box",
  borderBottom: `1px solid ${mercuryAlpha[8]}`,
});

const cellSx = {
  display: "flex",
  alignItems: "center",
  minWidth: 0,
  fontFamily: FONT,
  fontSize: 14,
  fontWeight: 400,
  lineHeight: 1.35,
  color: mercuryAlpha[65],
};

// Two-line cell: bold title with a small description under it.
export const TableCellTitle = ({ title, description }) => (
  <Box
    sx={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}
  >
    <Box
      sx={{
        ...ellipsisSx,
        fontFamily: FONT,
        fontSize: 14,
        fontWeight: 600,
        lineHeight: 1.3,
        color: mercury[950],
      }}
    >
      {title}
    </Box>
    {description && (
      <Box
        sx={{
          ...ellipsisSx,
          fontFamily: FONT,
          fontSize: 12,
          fontWeight: 400,
          lineHeight: 1.25,
          color: mercuryAlpha[55],
        }}
      >
        {description}
      </Box>
    )}
  </Box>
);

const headerLabelSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: "4px",
  minWidth: 0,
  fontFamily: FONT,
  fontSize: 12,
  fontWeight: 600,
  lineHeight: 1.3,
  letterSpacing: 0,
  color: mercuryAlpha[65],
};

/**
 * Table
 *
 * A data table card: toolbar (title, count, search, actions), sortable header row,
 * rows, with optional row selection and a row-actions column.
 *
 * Props:
 * - columns: [{ id, header, render?(row), accessor?, width?, sortable?, strong? }]
 *   width: number (px) or a CSS grid size (default minmax(120px, 1fr)).
 *   Without render, the cell shows row[accessor || id] as secondary text; strong makes it
 *   a medium-weight primary-colour label. render can return any node.
 * - rows: array of row objects
 * - rowKey: key field name or (row) => key (default "id")
 * - title, count: toolbar title and the count pill next to it
 * - search: { value, onChange, placeholder } props for the toolbar SearchField
 * - actions: node shown at the right of the toolbar (Buttons, IconButton, ...)
 * - sort: { by, direction: "asc" | "desc" } and onSort({ by, direction }) for sortable columns
 * - selectedKeys + onSelectionChange(keys): turns on the checkbox column
 * - rowActions: (row) => node for the last column (e.g. an IconButton)
 * - onRowClick: (row) => void
 * - loading, error (string), emptyText
 * - sx: extra styles for the card, merged last
 */
const Table = ({
  columns = [],
  rows = [],
  rowKey = "id",
  title,
  count,
  search,
  actions,
  sort,
  onSort,
  selectedKeys,
  onSelectionChange,
  rowActions,
  onRowClick,
  loading = false,
  error,
  emptyText = "No records",
  sx: sxOverride = {},
}) => {
  const getKey = typeof rowKey === "function" ? rowKey : (row) => row[rowKey];
  const selectable = Boolean(selectedKeys && onSelectionChange);
  const template = [
    selectable && SELECTION_WIDTH,
    ...columns.map((column) =>
      typeof column.width === "number"
        ? `${column.width}px`
        : column.width || DEFAULT_COLUMN_WIDTH,
    ),
    rowActions && ACTIONS_WIDTH,
  ]
    .filter(Boolean)
    .join(" ");

  const keys = rows.map(getKey);
  const selectedCount = keys.filter((key) =>
    selectedKeys?.includes(key),
  ).length;
  const allSelected = keys.length > 0 && selectedCount === keys.length;

  const toggleAll = () => onSelectionChange(allSelected ? [] : keys);
  const toggleRow = (key) =>
    onSelectionChange(
      selectedKeys.includes(key)
        ? selectedKeys.filter((k) => k !== key)
        : [...selectedKeys, key],
    );

  const handleSort = (columnId) =>
    onSort?.({
      by: columnId,
      direction:
        sort?.by === columnId && sort.direction === "asc" ? "desc" : "asc",
    });

  const renderCell = (column, row) => {
    if (column.render) return column.render(row);
    const value = row[column.accessor || column.id];
    return (
      <Box
        component="span"
        sx={{
          ...ellipsisSx,
          ...(column.strong && { fontWeight: 500, color: mercury[950] }),
        }}
      >
        {value}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        overflow: "hidden",
        boxSizing: "border-box",
        borderRadius: "12px",
        backgroundColor: colors.white,
        border: `1px solid ${mercuryAlpha[8]}`,
        boxShadow: "0 4px 16px rgba(16, 24, 40, 0.04)",
        ...sxOverride,
      }}
    >
      {(title || search || actions) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
            minHeight: 64,
            padding: "12px 16px",
            boxSizing: "border-box",
            borderBottom: `1px solid ${mercuryAlpha[8]}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {title && (
              <Box
                component="h2"
                sx={{
                  margin: 0,
                  fontFamily: FONT,
                  fontSize: 18,
                  fontWeight: 400,
                  lineHeight: 1.25,
                  color: mercury[950],
                }}
              >
                {title}
              </Box>
            )}
            {count !== undefined && count !== null && (
              <Box
                component="span"
                sx={{
                  padding: "2px 7px",
                  borderRadius: "999px",
                  backgroundColor: mercuryAlpha[8],
                  fontFamily: FONT,
                  fontSize: 11,
                  fontWeight: 600,
                  lineHeight: "14px",
                  color: mercuryAlpha[65],
                }}
              >
                {count}
              </Box>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
            {search && (
              <SearchField variant="filled" sx={{ width: 248 }} {...search} />
            )}
            {actions}
          </Box>
        </Box>
      )}

      <Box sx={{ overflowX: "auto" }}>
        <Box role="table" sx={{ width: "max-content", minWidth: "100%" }}>
          <Box
            role="row"
            sx={{ ...rowSx(template, 40), backgroundColor: mercuryAlpha[8] }}
          >
            {selectable && (
              <Box role="columnheader" sx={{ display: "flex" }}>
                <Checkbox
                  aria-label="Select all rows"
                  checked={allSelected}
                  indeterminate={selectedCount > 0 && !allSelected}
                  onChange={toggleAll}
                />
              </Box>
            )}
            {columns.map((column) => {
              const active = sort?.by === column.id;
              const SortIcon = !active
                ? UnfoldMoreIcon
                : sort.direction === "asc"
                  ? ArrowUpwardIcon
                  : ArrowDownwardIcon;
              return (
                <Box
                  key={column.id}
                  role="columnheader"
                  aria-sort={
                    active
                      ? sort.direction === "asc"
                        ? "ascending"
                        : "descending"
                      : undefined
                  }
                  sx={{ display: "flex", minWidth: 0 }}
                >
                  {column.sortable ? (
                    <ButtonBase
                      onClick={() => handleSort(column.id)}
                      sx={{
                        ...headerLabelSx,
                        color: active ? mercury[950] : mercuryAlpha[65],
                      }}
                    >
                      {column.header}
                      <SortIcon
                        aria-hidden="true"
                        sx={{
                          fontSize: 14,
                          color: active ? mercury[950] : mercuryAlpha[55],
                        }}
                      />
                    </ButtonBase>
                  ) : (
                    <Box component="span" sx={headerLabelSx}>
                      {column.header}
                    </Box>
                  )}
                </Box>
              );
            })}
            {rowActions && <Box role="columnheader" aria-label="Actions" />}
          </Box>

          {error && (
            <Box sx={{ padding: "16px" }}>
              <Banner tone="error" title="Could not load records">
                {error}
              </Banner>
            </Box>
          )}

          {!error &&
            loading &&
            Array.from({ length: SKELETON_ROWS }, (_, index) => (
              <Box key={index} role="row" sx={rowSx(template, 64)}>
                {template.split(" ").map((_size, cell) => (
                  <Skeleton key={cell} variant="text" sx={{ fontSize: 14 }} />
                ))}
              </Box>
            ))}

          {!error &&
            !loading &&
            rows.map((row) => {
              const key = getKey(row);
              const selected = Boolean(selectedKeys?.includes(key));
              return (
                <Box
                  key={key}
                  role="row"
                  aria-selected={selectable ? selected : undefined}
                  tabIndex={onRowClick ? 0 : undefined}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  onKeyDown={
                    onRowClick
                      ? (event) => event.key === "Enter" && onRowClick(row)
                      : undefined
                  }
                  sx={{
                    ...rowSx(template, 64),
                    backgroundColor: selected ? brand[50] : colors.white,
                    cursor: onRowClick ? "pointer" : "default",
                  }}
                >
                  {selectable && (
                    <Box
                      role="cell"
                      onClick={(event) => event.stopPropagation()}
                      sx={{ display: "flex" }}
                    >
                      <Checkbox
                        aria-label="Select row"
                        checked={selected}
                        onChange={() => toggleRow(key)}
                      />
                    </Box>
                  )}
                  {columns.map((column) => (
                    <Box key={column.id} role="cell" sx={cellSx}>
                      {renderCell(column, row)}
                    </Box>
                  ))}
                  {rowActions && (
                    <Box
                      role="cell"
                      onClick={(event) => event.stopPropagation()}
                      sx={{ display: "flex" }}
                    >
                      {rowActions(row)}
                    </Box>
                  )}
                </Box>
              );
            })}

          {!error && !loading && rows.length === 0 && (
            <Box
              sx={{
                padding: "48px 16px",
                textAlign: "center",
                fontFamily: FONT,
                fontSize: 14,
                lineHeight: 1.35,
                color: mercuryAlpha[55],
              }}
            >
              {emptyText}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Table;
